import { CheckCircle2, FileSpreadsheet, Merge, RefreshCw, Upload } from "lucide-react";
import { FormEvent, useState } from "react";

import api from "../api/client";
import { ErrorNotice, SuccessNotice } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import { apiErrorMessage } from "../utils/apiError";

type ImportPreview = {
  file_name: string;
  summary?: {
    row_count?: number;
  };
  conflicts?: unknown[];
};

type ImportResult = {
  created: number;
  updated: number;
  skipped: number;
};

export default function ImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const upload = async (event: FormEvent) => {
    event.preventDefault();
    if (!file) {
      return;
    }

    setLoading(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await api.post("/imports/upload/", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSessionId(response.data.session_id);
      setPreview(response.data.preview);
      setResult(null);
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het bestand kon niet worden gecontroleerd."),
      );
    } finally {
      setLoading(false);
    }
  };

  const commit = async (strategy: "skip" | "merge" | "overwrite") => {
    if (!sessionId) {
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await api.post(`/imports/sessions/${sessionId}/commit/`, {
        strategy,
      });
      setResult(response.data);
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De import kon niet worden verwerkt."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="import-page">
      <PageHeader
        eyebrow="Gegevensbeheer"
        title="Excel importeren"
        description="Importeer verlofregels vanuit een gecontroleerd CSV- of Excel-bestand."
      />
      {error && <ErrorNotice>{error}</ErrorNotice>}
      <section className="surface import-panel">
        <div className="surface-heading">
          <div>
            <p className="eyebrow">Stap 1</p>
            <h2>Selecteer een bestand</h2>
          </div>
          <FileSpreadsheet size={22} />
        </div>
        <form className="import-form" onSubmit={upload}>
          <label className="file-picker">
            <Upload size={24} />
            <span>
              <strong>{file?.name ?? "Kies een bestand"}</strong>
              <small>CSV of XLSX</small>
            </span>
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </label>
          <button className="button primary" type="submit" disabled={!file || loading}>
            <Upload size={17} />{" "}
            {loading ? "Controleren..." : "Uploaden en controleren"}
          </button>
        </form>
      </section>

      {preview && (
        <section className="surface import-panel">
          <div className="surface-heading">
            <div>
              <p className="eyebrow">Stap 2</p>
              <h2>Controle en conflicten</h2>
            </div>
            <span className="file-name">{preview.file_name}</span>
          </div>
          <div className="import-metrics">
            <div>
              <span>Rijen</span>
              <strong>{preview.summary?.row_count ?? 0}</strong>
            </div>
            <div>
              <span>Conflicten</span>
              <strong>{preview.conflicts?.length ?? 0}</strong>
            </div>
          </div>
          <div className="import-actions" aria-label="Importstrategie">
            <button
              className="button subtle"
              disabled={loading}
              onClick={() => void commit("skip")}
            >
              <CheckCircle2 size={17} /> Dubbelen overslaan
            </button>
            <button
              className="button subtle"
              disabled={loading}
              onClick={() => void commit("merge")}
            >
              <Merge size={17} /> Samenvoegen
            </button>
            <button
              className="button danger-outline"
              disabled={loading}
              onClick={() => void commit("overwrite")}
            >
              <RefreshCw size={17} /> Overschrijven
            </button>
          </div>
        </section>
      )}

      {result && (
        <section className="surface import-panel">
          <SuccessNotice>De import is afgerond.</SuccessNotice>
          <div className="import-metrics result">
            <div>
              <span>Aangemaakt</span>
              <strong>{result.created}</strong>
            </div>
            <div>
              <span>Bijgewerkt</span>
              <strong>{result.updated}</strong>
            </div>
            <div>
              <span>Overgeslagen</span>
              <strong>{result.skipped}</strong>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
